// Critical-path view (wireframe 04) — five-model comparison.
//
// Layout:
//   - Tabs (5 models): Classical / Monte Carlo / RCPM / CCPM / Resource bottleneck
//   - KPI strip for the active model
//   - Iframe of cp_<model>.html?chrome=false (inner nav stripped)
//   - Compare-across-all-5-models table

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShell } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

type ModelId = "classical" | "monte_carlo" | "rcpm" | "ccpm" | "resource_diagram";
const VALID_MODELS: ModelId[] = ["classical", "monte_carlo", "rcpm", "ccpm", "resource_diagram"];

interface ModelTab { id: ModelId; label: string; }
const MODELS: ModelTab[] = [
  { id: "classical",        label: "Classical CPM" },
  { id: "monte_carlo",      label: "Monte Carlo" },
  { id: "rcpm",             label: "Resource-constrained" },
  { id: "ccpm",             label: "Critical chain (buffers)" },
  { id: "resource_diagram", label: "Resource bottleneck" },
];

interface ModelRow {
  id: ModelId;
  label: string;
  assumes: string;
  available: boolean;
  project_end: string | null;
  critical_count: number | null;
  monte_carlo?: {
    p10: string | null;
    p50: string | null;
    p80: string | null;
    p95: string | null;
    simulations: number | null;
  };
  note?: string;
}

export default function CriticalPathView() {
  const { project } = useShell();
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed from ?model= so the chat agent can deep-link to a non-default
  // model. Falls back to rcpm — matches the wireframe default.
  const urlModel = searchParams.get("model");
  const initial: ModelId = (VALID_MODELS as string[]).includes(urlModel || "")
    ? (urlModel as ModelId) : "rcpm";
  const [model, setModel] = useState<ModelId>(initial);
  // React when ?model= changes (e.g. chat agent re-renders a different
  // model and the router navigates back to /critical-path?model=ccpm).
  useEffect(() => {
    if (urlModel && (VALID_MODELS as string[]).includes(urlModel) && urlModel !== model) {
      setModel(urlModel as ModelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlModel]);
  // Mirror tab clicks back into the URL so the route is shareable.
  useEffect(() => {
    if (searchParams.get("model") !== model) {
      const next = new URLSearchParams(searchParams);
      next.set("model", model);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);
  const summary = useTool("critical_path_summary", { project });
  const render = useTool("render_critical_path", { project, model });
  const rows = ((summary.env?.data as { models?: ModelRow[] } | undefined)?.models ?? []) as ModelRow[];
  const activeRow = useMemo(() => rows.find((r) => r.id === model) ?? null, [rows, model]);
  const iframeUrl = (render.env?.data as { url?: string } | undefined)?.url ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1 text-xs">
        {MODELS.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setModel(m.id)}
            className={
              "rounded px-3 py-1 " +
              (model === m.id ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")
            }
          >{m.label}</button>
        ))}
      </div>

      {/* KPI strip for active model */}
      <KpiStrip model={model} row={activeRow} />

      {/* Iframe of cp_<model>.html?chrome=false */}
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {render.loading ? <Skeleton lines={4} /> :
         render.error ? <ErrorState error={render.error} onRetry={render.refetch} /> :
         iframeUrl ? (
           // key={iframeUrl} forces React to remount the iframe whenever
           // the model tab changes (instead of reusing the DOM node and
           // just swapping `src`, which races against the previous load
           // and sometimes leaves the iframe blank — see Bug #3, 2026-05-20).
           // `&_=Date.now()` appended so the browser never serves a half-
           // cached / stale response from a previous request.
           <iframe key={iframeUrl}
                   src={iframeUrl + (iframeUrl.includes("?") ? "&" : "?") + "_=" + Date.now()}
                   className="h-full min-h-[400px] w-full border-0"
                   title={`Critical path · ${model}`} />
         ) : (
           <div className="p-4 text-sm text-slate-500">
             No report URL returned for {model}.
           </div>
         )}
      </div>

      {/* Compare across all 5 models */}
      <CompareTable rows={rows} loading={summary.loading} error={summary.error}
                    refetch={summary.refetch} activeId={model} onPick={setModel} />
    </div>
  );
}

function KpiStrip({ model, row }: { model: ModelId; row: ModelRow | null }) {
  if (!row) return <div className="text-xs text-slate-500">Loading KPIs…</div>;
  if (!row.available) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        cp_{model}.html not yet generated. Click <b>Rebuild</b> to compute this model.
      </div>
    );
  }
  if (model === "monte_carlo" && row.monte_carlo) {
    const mc = row.monte_carlo;
    return (
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <KpiCard label="Simulations" value={mc.simulations?.toLocaleString() ?? "—"} />
        <KpiCard label="P50 end" value={mc.p50 ?? "—"} sub={mc.p10 ? `P10 = ${mc.p10}` : undefined} />
        <KpiCard label="P80 end" value={mc.p80 ?? "—"} sub={driftLabel(mc.p80, mc.p50)} subTone="amber" />
        <KpiCard label="P95 end" value={mc.p95 ?? "—"} sub={driftLabel(mc.p95, mc.p50)} subTone="rose" />
        <KpiCard label="Critical tasks" value={String(row.critical_count ?? "—")}
                 sub="≥ deterministic crit-index" />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
      <KpiCard label="Project end" value={row.project_end ?? "—"} sub={row.assumes} />
      <KpiCard label="Critical tasks" value={String(row.critical_count ?? "—")} />
      <KpiCard label="Assumes" value={row.assumes} small />
    </div>
  );
}

function KpiCard({ label, value, sub, subTone, small }: {
  label: string;
  value: string;
  sub?: string;
  subTone?: "amber" | "rose";
  small?: boolean;
}) {
  const subClass =
    subTone === "rose" ? "text-rose-600" :
    subTone === "amber" ? "text-amber-600" :
                          "text-slate-500";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={small ? "mt-1 text-sm font-medium text-slate-700" : "mt-1 text-xl font-semibold"}>
        {value}
      </div>
      {sub && <div className={`text-xs ${subClass}`}>{sub}</div>}
    </div>
  );
}

function driftLabel(later: string | null, earlier: string | null): string | undefined {
  if (!later || !earlier) return undefined;
  const days = Math.round(
    (new Date(later).getTime() - new Date(earlier).getTime()) / 86_400_000,
  );
  if (days === 0) return "= P50";
  if (days > 0) return `+${days} d vs P50`;
  return `${days} d vs P50`;
}

function CompareTable({ rows, loading, error, refetch, activeId, onPick }: {
  rows: ModelRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  activeId: ModelId;
  onPick: (id: ModelId) => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">
        Compare end-date across all 5 models
      </div>
      {loading ? <Skeleton lines={3} /> :
       error ? <ErrorState error={error} onRetry={refetch} /> : (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="text-left font-normal">model</th>
              <th className="text-left font-normal">assumes</th>
              <th className="text-right font-normal">project end</th>
              <th className="text-right font-normal">critical tasks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className={
                  "cursor-pointer border-b border-slate-50 last:border-b-0 " +
                  (r.id === activeId ? "bg-sky-50" : "hover:bg-slate-50")
                }
                onClick={() => onPick(r.id)}
              >
                <td className="py-1.5 pr-2 text-slate-800">{r.label}</td>
                <td className="pr-2 text-slate-600">{r.assumes}</td>
                <td className="py-1.5 text-right font-mono text-slate-700">
                  {r.project_end ?? (r.available ? (r.note ?? "—") : "(not built)")}
                </td>
                <td className="py-1.5 text-right">
                  {r.id === "monte_carlo" && r.critical_count != null
                    ? `${r.critical_count} (CI ≥ 80%)`
                    : r.critical_count ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
